import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  FiAlertCircle,
  FiBell,
  FiChevronDown,
  FiMessageSquare,
  FiSearch,
} from "react-icons/fi";
import { HiOutlineArchiveBox, HiOutlineTrash } from "react-icons/hi2";
import Spinner from "../../../components/Spinner";
import api from "../../../utils/api";
import { IAuth } from "../../../types/authTypes";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import styles from "../styles/CreatorMessages.module.scss";
import type {
  ConversationsResponse,
  CreatorNotification,
  CustomerMessage,
  MessageTab,
  NotificationCategory,
  NotificationsResponse,
} from "../types/messages.types";
import {
  mapConversationToCustomerMessage,
  mapNotificationToView,
} from "../utils/messagesMappers";

const FILTER_OPTIONS: { value: NotificationCategory; label: string }[] = [
  { value: "all", label: "All" },
  { value: "orders", label: "Orders" },
  { value: "reviews", label: "Reviews" },
  { value: "system", label: "System" },
];

const SEARCH_DEBOUNCE_MS = 400;

const getErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const response = (err as { response?: { data?: { message?: string; msg?: string } } })
      .response;
    return response?.data?.message || response?.data?.msg || fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
};

export default function CreatorMessages() {
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);

  const [activeTab, setActiveTab] = useState<MessageTab>("notifications");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const [categoryFilter, setCategoryFilter] = useState<NotificationCategory>("all");

  const [notifications, setNotifications] = useState<CreatorNotification[]>([]);
  const [messages, setMessages] = useState<CustomerMessage[]>([]);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);

  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async (searchQuery: string, category: NotificationCategory) => {
    setLoadingNotifications(true);
    setError(null);

    try {
      const { data } = await api.get<NotificationsResponse>("/notifications", {
        params: {
          page: 1,
          limit: 50,
          search: searchQuery.trim() || undefined,
          category: category === "all" ? undefined : category,
        },
      });

      if (!data.success) {
        throw new Error(data.message || "Failed to load notifications");
      }

      const mapped = (data.data.notifications || []).map(mapNotificationToView);
      setNotifications(mapped);
      setNotificationUnreadCount(data.data.unreadCount ?? 0);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load notifications"));
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  const fetchConversations = useCallback(
    async (searchQuery: string) => {
      if (!user?._id) {
        setLoadingMessages(false);
        return;
      }

      setLoadingMessages(true);
      setError(null);

      try {
        const { data } = await api.get<ConversationsResponse>("/messages", {
          params: {
            search: searchQuery.trim() || undefined,
          },
        });

        if (!data.success) {
          throw new Error(data.message || "Failed to load messages");
        }

        const conversations = data.data.conversations || [];
        const mapped = conversations.map((conversation) =>
          mapConversationToCustomerMessage(conversation, user._id)
        );

        setMessages(mapped);
        setMessageUnreadCount(data.data.unreadCount ?? 0);
      } catch (err) {
        setError(getErrorMessage(err, "Failed to load messages"));
      } finally {
        setLoadingMessages(false);
      }
    },
    [user?._id]
  );

  useEffect(() => {
    fetchNotifications(debouncedSearch, categoryFilter);
  }, [debouncedSearch, categoryFilter, fetchNotifications]);

  useEffect(() => {
    fetchConversations(debouncedSearch);
  }, [debouncedSearch, fetchConversations]);

  const handleRemoveNotification = async (id: string) => {
    setActionError(null);

    try {
      await api.delete(`/notifications/${id}`);
      await fetchNotifications(debouncedSearch, categoryFilter);
    } catch (err) {
      setActionError(getErrorMessage(err, "Failed to delete notification"));
    }
  };

  const handleArchiveNotification = async (id: string) => {
    setActionError(null);

    try {
      await api.patch(`/notifications/${id}/read`);
      await fetchNotifications(debouncedSearch, categoryFilter);
    } catch (err) {
      setActionError(getErrorMessage(err, "Failed to archive notification"));
    }
  };

  const handleTabChange = (tab: MessageTab) => {
    setActiveTab(tab);
    setSearch("");
    setActionError(null);
  };

  const handleCategoryChange = (category: NotificationCategory) => {
    setCategoryFilter(category);
  };

  const isNotifications = activeTab === "notifications";
  const searchPlaceholder = isNotifications
    ? "Search notifications..."
    : "Search messages...";

  const listItems = isNotifications ? notifications : messages;
  const isListLoading = isNotifications ? loadingNotifications : loadingMessages;
  const isInitialListLoading = isListLoading && listItems.length === 0;
  const isListRefreshing = isListLoading && listItems.length > 0;

  const renderListContent = () => {
    if (isNotifications) {
      if (notifications.length === 0) {
        return <p className={styles.emptyState}>No notifications found.</p>;
      }
      return notifications.map((n) => (
        <article key={n.id} className={styles.notificationItem}>
          <div className={styles.notificationIcon} aria-hidden>
            <FiAlertCircle size={18} />
          </div>
          <div className={styles.notificationBody}>
            <h2 className={styles.notificationTitle}>{n.title}</h2>
            <p className={styles.notificationText}>{n.body}</p>
            <time className={styles.notificationTime}>{n.timestamp}</time>
          </div>
          <div className={styles.notificationActions}>
            <button
              type="button"
              className={styles.actionBtn}
              aria-label="Mark notification as read"
              onClick={() => handleArchiveNotification(n.id)}
              disabled={n.isRead}
            >
              <HiOutlineArchiveBox size={18} />
            </button>
            <button
              type="button"
              className={styles.actionBtnDanger}
              aria-label="Delete notification"
              onClick={() => handleRemoveNotification(n.id)}
            >
              <HiOutlineTrash size={18} />
            </button>
          </div>
        </article>
      ));
    }

    if (messages.length === 0) {
      return <p className={styles.emptyState}>No messages found.</p>;
    }

    return messages.map((m) => (
      <article key={m.conversationId} className={styles.messageItem}>
        <div className={styles.messageRow}>
          <div className={styles.avatar} aria-hidden>
            {m.initials}
          </div>
          <div className={styles.messageContent}>
            <div className={styles.messageHeader}>
              <h2 className={styles.senderName}>{m.sender}</h2>
              <div className={styles.messageMeta}>
                <time className={styles.timestamp}>{m.timestamp}</time>
                {m.isUnread && <span className={styles.unreadDot} aria-label="Unread" />}
              </div>
            </div>
            <p className={styles.subject}>{m.subject}</p>
            <p className={styles.preview}>{m.body}</p>
            <div className={styles.messageFooter}>
              <span className={styles.productTag}>Re: {m.product}</span>
              <button type="button" className={styles.replyBtn}>
                Reply
              </button>
            </div>
          </div>
        </div>
      </article>
    ));
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Messages &amp; Notifications</h1>
        <p className={styles.subtitle}>
          Stay connected with your customers and platform updates
        </p>
      </header>

      <div className={styles.tabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={isNotifications}
          className={`${styles.tab} ${isNotifications ? styles.tabActive : ""}`}
          onClick={() => handleTabChange("notifications")}
        >
          <span className={styles.tabIcon} aria-hidden>
            <FiBell />
          </span>
          Notifications
          {notificationUnreadCount > 0 && (
            <span className={styles.badge}>{notificationUnreadCount}</span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={!isNotifications}
          className={`${styles.tab} ${!isNotifications ? styles.tabActive : ""}`}
          onClick={() => handleTabChange("messages")}
        >
          <span className={styles.tabIcon} aria-hidden>
            <FiMessageSquare />
          </span>
          Customer Messages
          {messageUnreadCount > 0 && (
            <span className={styles.badge}>{messageUnreadCount}</span>
          )}
        </button>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchWrapper}>
          <span className={styles.searchIcon}>
            <FiSearch size={15} />
          </span>
          <input
            type="search"
            className={styles.searchInput}
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={searchPlaceholder}
          />
        </div>

        {isNotifications && (
          <div className={styles.filterWrapper}>
            <select
              className={styles.filterSelect}
              value={categoryFilter}
              onChange={(e) =>
                handleCategoryChange(e.target.value as NotificationCategory)
              }
              aria-label="Filter notifications"
            >
              {FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className={styles.filterChevron}>
              <FiChevronDown size={12} />
            </span>
          </div>
        )}
      </div>

      {(error || actionError) && (
        <p className={styles.errorBanner} role="alert">
          {error || actionError}
        </p>
      )}

      <div className={styles.listCard} aria-busy={isListLoading}>
        {isInitialListLoading ? (
          <div className={styles.loadingState}>
            <Spinner />
          </div>
        ) : (
          <>
            {isListRefreshing && (
              <div className={styles.listOverlay} aria-hidden>
                <Spinner />
              </div>
            )}
            <div
              className={
                isListRefreshing ? styles.listContentDimmed : styles.listContent
              }
            >
              {renderListContent()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
